/**
 * Typed fetch client (FOUNDATION — BUILD-PLAN src/lib/api.ts).
 *
 * Typed against `@humble/shared` api-types (generated from openapi.yaml —
 * regenerate there, never hand-edit). Contract behaviors implemented here so
 * no vertical re-invents them:
 *
 *  - Auth: Bearer access token from the in-memory session store; the refresh
 *    token is an httpOnly cookie (path /api/v1/auth). On a 401 from any
 *    authed endpoint the client single-flights POST /auth/refresh and retries
 *    the original request ONCE with the rotated token; a failed refresh
 *    clears the session (the auth gate then routes to the landing).
 *  - Idempotency: state-machine mutations REQUIRE client_event_id (SM §0).
 *    Build bodies with `withEventId(...)` — retries (including the 401-refresh
 *    retry) reuse the SAME body object, so the id is stable across retries.
 *  - Errors: every non-2xx is the normative envelope {code, message,
 *    request_id, reset_at?, details?} → thrown as ApiError. `©errors.500`
 *    already carries the request_id interpolated server-side.
 *
 * Usage (a vertical adding a screen):
 *   const deck = await api.get("/deck");
 *   const res  = await api.post("/swipes", { body: withEventId({ target_id, verdict: "reject" }) });
 *   const msgs = await api.get("/matches/{matchId}/messages", {
 *     params: { matchId }, query: { after_seq: 42 } });
 */
import type { ApiPaths } from "@humble/shared";
import { clearSession, sessionStore, setSession } from "./store.js";

export const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// type plumbing over the generated openapi-typescript shapes
// ---------------------------------------------------------------------------
type Method = "get" | "post" | "put" | "patch" | "delete";

/** Paths that define the given method (absent methods are `get?: never`). */
export type PathsWith<M extends Method> = {
  [P in keyof ApiPaths]: ApiPaths[P] extends Record<M, unknown> ? P : never;
}[keyof ApiPaths];

type OperationOf<P extends keyof ApiPaths, M extends Method> =
  ApiPaths[P] extends Record<M, infer Op> ? Op : never;

type SuccessStatus = 200 | 201 | 202 | 204;
type ResponsesOf<Op> = Op extends { responses: infer R } ? R : never;
type JsonBody<T> = T extends { content: { "application/json": infer J } } ? J : undefined;
/** Union of the JSON bodies of every 2xx response (204 ⇒ undefined). */
type SuccessOf<Op> = {
  [S in Extract<keyof ResponsesOf<Op>, SuccessStatus>]: JsonBody<ResponsesOf<Op>[S]>;
}[Extract<keyof ResponsesOf<Op>, SuccessStatus>];

/** JSON request body of an operation (never ⇒ endpoint takes no body). */
type BodyOf<Op> = Op extends { requestBody?: infer RB }
  ? RB extends { content: { "application/json": infer B } }
    ? B
    : never
  : never;

export interface RequestOptions<B> {
  /** Path template params, e.g. { matchId } for "/matches/{matchId}". */
  params?: Record<string, string | number>;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: B;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// error envelope
// ---------------------------------------------------------------------------
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly resetAt: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    envelope: {
      code?: string;
      message?: string;
      request_id?: string;
      reset_at?: string;
      details?: Record<string, unknown>;
    },
  ) {
    super(envelope.message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = envelope.code ?? "UNKNOWN";
    this.requestId = envelope.request_id;
    this.resetAt = envelope.reset_at;
    this.details = envelope.details;
  }
}

export function isApiError(err: unknown, code?: string): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}

// ---------------------------------------------------------------------------
// idempotency + device fingerprint helpers
// ---------------------------------------------------------------------------
/**
 * Stamp a state-machine mutation body with its client_event_id (SM §0).
 * Build the body ONCE and reuse the object on retries — the id must not
 * change between attempts of the same logical event.
 */
export function withEventId<T extends { client_event_id: string }>(
  body: Omit<T, "client_event_id">,
): T {
  return { ...body, client_event_id: crypto.randomUUID() } as T;
}

const DEVICE_FP_KEY = "humble.device_fp";
/**
 * Client device fingerprint — deliberately just a persisted random id
 * (ARCH §6: the value is attacker-controlled by nature and the server treats
 * it as a speed bump; the serious friction is server-side).
 */
export function getDeviceFp(): string {
  try {
    let fp = localStorage.getItem(DEVICE_FP_KEY);
    if (!fp) {
      fp = crypto.randomUUID();
      localStorage.setItem(DEVICE_FP_KEY, fp);
    }
    return fp;
  } catch {
    return "ephemeral-" + crypto.randomUUID();
  }
}

// ---------------------------------------------------------------------------
// refresh-on-401 (single-flight; rotation grace handled server-side)
// ---------------------------------------------------------------------------
interface TokenPairBody {
  access_token?: string;
  expires_in_s?: number;
  lifecycle?: string;
  user?: import("./store.js").UserRef;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Rotate the refresh cookie into a fresh access token. Returns true on
 * success. Safe to call concurrently — all callers share one attempt.
 */
export async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        clearSession();
        return false;
      }
      const pair = (await res.json()) as TokenPairBody;
      if (!pair.access_token) {
        clearSession();
        return false;
      }
      setSession(pair.access_token, pair.user ?? sessionStore.get().user, pair.lifecycle ?? null);
      return true;
    } catch {
      return false; // network failure: keep session state as-is, caller sees the original error
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// core request
// ---------------------------------------------------------------------------
function buildUrl(
  path: string,
  params?: Record<string, string | number>,
  query?: RequestOptions<unknown>["query"],
): string {
  let url = path.replace(/\{(\w+)\}/g, (_m, name: string) => {
    const v = params?.[name];
    if (v === undefined) throw new Error(`api: missing path param {${name}} for ${path}`);
    return encodeURIComponent(String(v));
  });
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  return API_BASE + url;
}

async function request<T>(
  method: Method,
  path: string,
  opts: RequestOptions<unknown> = {},
  retried = false,
): Promise<T> {
  const token = sessionStore.get().accessToken;
  const headers: Record<string, string> = { ...opts.headers };
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (token) headers["authorization"] = `Bearer ${token}`;

  const res = await fetch(buildUrl(path, opts.params, opts.query), {
    method: method.toUpperCase(),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "include", // the auth refresh cookie is scoped to /api/v1/auth
    signal: opts.signal ?? null,
  });

  if (res.status === 401 && !retried && token && !path.startsWith("/auth/")) {
    // access token expired or auth_version bumped — one refresh, one retry.
    const ok = await refreshSession();
    if (ok) return request<T>(method, path, opts, true);
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let envelope: ConstructorParameters<typeof ApiError>[1] = {};
    try {
      envelope = (await res.json()) as typeof envelope;
    } catch {
      envelope = { code: "HTTP_" + res.status, message: res.statusText };
    }
    throw new ApiError(res.status, envelope);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ---------------------------------------------------------------------------
// public surface — one typed method per HTTP verb
// ---------------------------------------------------------------------------
export const api = {
  get<P extends PathsWith<"get">>(
    path: P,
    opts?: RequestOptions<never>,
  ): Promise<SuccessOf<OperationOf<P, "get">>> {
    return request("get", path, opts);
  },
  post<P extends PathsWith<"post">>(
    path: P,
    opts?: RequestOptions<BodyOf<OperationOf<P, "post">>>,
  ): Promise<SuccessOf<OperationOf<P, "post">>> {
    return request("post", path, opts);
  },
  put<P extends PathsWith<"put">>(
    path: P,
    opts?: RequestOptions<BodyOf<OperationOf<P, "put">>>,
  ): Promise<SuccessOf<OperationOf<P, "put">>> {
    return request("put", path, opts);
  },
  patch<P extends PathsWith<"patch">>(
    path: P,
    opts?: RequestOptions<BodyOf<OperationOf<P, "patch">>>,
  ): Promise<SuccessOf<OperationOf<P, "patch">>> {
    return request("patch", path, opts);
  },
  delete<P extends PathsWith<"delete">>(
    path: P,
    opts?: RequestOptions<BodyOf<OperationOf<P, "delete">>>,
  ): Promise<SuccessOf<OperationOf<P, "delete">>> {
    return request("delete", path, opts);
  },
} as const;

/**
 * Session bootstrap for app start: tries the refresh cookie once. Resolves
 * the session store to "authed" or "anon" — never leaves it "unknown".
 */
export async function bootstrapSession(): Promise<void> {
  const ok = await refreshSession();
  if (!ok && sessionStore.get().status === "unknown") clearSession();
}
